import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Scale, Sparkles, FolderOpen, FileText, Search, Database,
  MessageSquare, ArrowRight, ArrowLeft, Check, X, Rocket
} from "lucide-react";
import { createProject } from "@/lib/api";

const ONBOARDING_KEY = "les_lex_onboarding_done";

const steps = [
  { id: 1, title: "Bienvenue sur LES-LEX", shortTitle: "Accueil" },
  { id: 2, title: "Cr\u00e9ez votre premier projet", shortTitle: "Projet" },
  { id: 3, title: "G\u00e9n\u00e9rateur de documents", shortTitle: "G\u00e9n\u00e9rateur" },
  { id: 4, title: "D\u00e9couvrez les fonctionnalit\u00e9s", shortTitle: "Fonctions" },
];

const documentTemplates = [
  { value: "Accord NDA", label: "Accord NDA", description: "Accord de confidentialit\u00e9" },
  { value: "Acte de proc\u00e9dure", label: "Acte de proc\u00e9dure", description: "Acte destin\u00e9 au tribunal" },
  { value: "Avis juridique", label: "Avis juridique", description: "Analyse de la situation juridique" },
  { value: "R\u00e8glement", label: "R\u00e8glement", description: "R\u00e8glement de service ou de prestation" },
  { value: "Contrat", label: "Contrat g\u00e9n\u00e9ral", description: "Tout type de contrat" },
];

const features = [
  {
    icon: Search,
    title: "Jurisprudence et l\u00e9gislation",
    description: "Recherchez dans la jurisprudence L\u00e9gifrance et les textes l\u00e9gislatifs avec analyse IA",
    path: "/legal-search",
    color: "text-blue-600 bg-blue-50",
  },
  {
    icon: Database,
    title: "Base de connaissances",
    description: "Construisez votre propre base de connaissances juridiques avec recherche RAG",
    path: "/knowledge-base",
    color: "text-emerald-600 bg-emerald-50",
  },
  {
    icon: MessageSquare,
    title: "Studio IA",
    description: "Discutez avec l'assistant IA sur les documents dans le contexte du projet",
    path: null,
    color: "text-violet-600 bg-violet-50",
  },
  {
    icon: FileText,
    title: "PDF Lab",
    description: "Analysez et traitez les documents PDF avec OCR",
    path: null,
    color: "text-amber-600 bg-amber-50",
  },
];

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  // Step 2 state
  const [projectName, setProjectName] = useState("");
  const [projectType, setProjectType] = useState("");
  const [creating, setCreating] = useState(false);
  const [createdProjectId, setCreatedProjectId] = useState(null);
  const [createError, setCreateError] = useState("");

  // Step 3 state
  const [selectedTemplate, setSelectedTemplate] = useState("");

  // Check if onboarding should show
  useEffect(() => {
    const done = localStorage.getItem(ONBOARDING_KEY);
    if (!done) {
      setOpen(true);
    }
  }, []);

  const progressPercent = (currentStep / steps.length) * 100;

  const handleComplete = () => {
    localStorage.setItem(ONBOARDING_KEY, "true");
    setOpen(false);
    if (createdProjectId) {
      navigate(`/project/${createdProjectId}/creator`);
    }
  };

  const handleSkip = () => {
    localStorage.setItem(ONBOARDING_KEY, "true");
    setOpen(false);
  };

  const handleCreateProject = async () => {
    if (!projectName.trim()) return;
    setCreating(true);
    setCreateError("");
    try {
      const res = await createProject({
        name: projectName.trim(),
        type: projectType || "Autre",
        description: "",
      });
      setCreatedProjectId(res.data.id);
      // Auto-advance to next step
      setCurrentStep(3);
    } catch (err) {
      setCreateError(err.response?.data?.detail || "\u00c9chec de la cr\u00e9ation du projet");
    } finally {
      setCreating(false);
    }
  };

  const handleFeatureClick = (path) => {
    if (path) {
      localStorage.setItem(ONBOARDING_KEY, "true");
      setOpen(false);
      navigate(path);
    }
  };

  const canGoNext = () => {
    if (currentStep === 2) return !!createdProjectId;
    return true;
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-[560px] p-0 gap-0 overflow-hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Progress bar */}
        <div className="px-6 pt-5 pb-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Scale className="h-5 w-5 text-primary" />
              <span className="font-display font-bold text-sm">LES-LEX</span>
            </div>
            <Button variant="ghost" size="sm" className="text-xs text-muted-foreground h-7" onClick={handleSkip}>
              <X className="h-3.5 w-3.5 mr-1" /> Passer
            </Button>
          </div>
          <Progress value={progressPercent} className="h-1.5" />
          <div className="flex justify-between mt-2">
            {steps.map((step) => (
              <span
                key={step.id}
                className={`text-[10px] font-medium ${
                  step.id <= currentStep ? "text-primary" : "text-muted-foreground"
                }`}
              >
                {step.shortTitle}
              </span>
            ))}
          </div>
        </div>

        {/* Step content */}
        <div className="px-6 pb-6 pt-2 min-h-[320px]">
          {/* Step 1: Welcome */}
          {currentStep === 1 && (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Rocket className="h-8 w-8 text-primary" />
              </div>
              <h2 className="font-display text-xl font-bold mb-2">Bienvenue sur LES-LEX !</h2>
              <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                Plateforme IA pour les juristes. G\u00e9n\u00e9rez des documents, analysez des contrats,
                recherchez la jurisprudence et construisez votre base de connaissances juridiques.
              </p>
              <div className="grid grid-cols-2 gap-3 max-w-sm mx-auto">
                {[
                  { icon: FileText, text: "G\u00e9n\u00e9ration de documents" },
                  { icon: Search, text: "Recherche juridique" },
                  { icon: Database, text: "Base de connaissances RAG" },
                  { icon: MessageSquare, text: "Assistant IA" },
                ].map((feature, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 rounded-lg border p-2.5 text-left"
                  >
                    <feature.icon className="h-4 w-4 text-primary shrink-0" />
                    <span className="text-xs font-medium">{feature.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Create project */}
          {currentStep === 2 && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                  <FolderOpen className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="font-display text-lg font-bold">Cr\u00e9ez votre premier projet</h2>
                  <p className="text-xs text-muted-foreground">Les projets organisent vos documents</p>
                </div>
              </div>

              {createdProjectId ? (
                <Card className="border-emerald-200 bg-emerald-50/50">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Check className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-emerald-800">Projet cr\u00e9\u00e9 !</p>
                      <p className="text-xs text-emerald-600">"{projectName}" est pr\u00eat \u00e0 utiliser</p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-1.5 block">Nom du projet</label>
                    <Input
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      placeholder="ex. Accord NDA — Client ABC"
                      className="h-11"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1.5 block">Type de document</label>
                    <Select value={projectType} onValueChange={setProjectType}>
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Choisir le type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Accord NDA">Accord NDA</SelectItem>
                        <SelectItem value="Acte de proc\u00e9dure">Acte de proc\u00e9dure</SelectItem>
                        <SelectItem value="Avis juridique">Avis juridique</SelectItem>
                        <SelectItem value="R\u00e8glement">R\u00e8glement</SelectItem>
                        <SelectItem value="Contrat">Contrat</SelectItem>
                        <SelectItem value="Autre">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {createError && (
                    <p className="text-sm text-destructive">{createError}</p>
                  )}
                  <Button
                    onClick={handleCreateProject}
                    disabled={!projectName.trim() || creating}
                    className="w-full h-11"
                  >
                    {creating ? (
                      <Sparkles className="h-4 w-4 mr-2 animate-pulse" />
                    ) : (
                      <FolderOpen className="h-4 w-4 mr-2" />
                    )}
                    {creating ? "Cr\u00e9ation..." : "Cr\u00e9er le projet"}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Document generator */}
          {currentStep === 3 && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-violet-600" />
                </div>
                <div>
                  <h2 className="font-display text-lg font-bold">G\u00e9n\u00e9rateur de documents</h2>
                  <p className="text-xs text-muted-foreground">Choisissez un mod\u00e8le et g\u00e9n\u00e9rez un document avec l'IA</p>
                </div>
              </div>

              <div className="space-y-2">
                {documentTemplates.map((template) => (
                  <button
                    key={template.value}
                    onClick={() => setSelectedTemplate(template.value)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                      selectedTemplate === template.value
                        ? "border-primary bg-primary/5 ring-1 ring-primary"
                        : "hover:bg-muted/50"
                    }`}
                  >
                    <FileText className={`h-5 w-5 shrink-0 ${
                      selectedTemplate === template.value ? "text-primary" : "text-muted-foreground"
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{template.label}</p>
                      <p className="text-xs text-muted-foreground">{template.description}</p>
                    </div>
                    {selectedTemplate === template.value && (
                      <Badge variant="default" className="text-[10px] shrink-0">S\u00e9lectionn\u00e9</Badge>
                    )}
                  </button>
                ))}
              </div>

              {selectedTemplate && (
                <p className="text-xs text-muted-foreground mt-3 text-center">
                  Le mod\u00e8le "{selectedTemplate}" sera disponible dans le cr\u00e9ateur de projet
                </p>
              )}
            </div>
          )}

          {/* Step 4: Explore features */}
          {currentStep === 4 && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="font-display text-lg font-bold">D\u00e9couvrez les fonctionnalit\u00e9s</h2>
                  <p className="text-xs text-muted-foreground">Cliquez sur une fonctionnalit\u00e9 pour l'essayer</p>
                </div>
              </div>

              <div className="space-y-2">
                {features.map((feature, i) => {
                  const FeatureIcon = feature.icon;
                  return (
                    <button
                      key={i}
                      onClick={() => handleFeatureClick(feature.path)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors text-left group"
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${feature.color}`}>
                        <FeatureIcon className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{feature.title}</p>
                        <p className="text-xs text-muted-foreground">{feature.description}</p>
                      </div>
                      {feature.path && (
                        <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Navigation buttons */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-muted/30">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCurrentStep((s) => Math.max(1, s - 1))}
            disabled={currentStep === 1}
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Retour
          </Button>

          <div className="flex items-center gap-1.5">
            {steps.map((step) => (
              <div
                key={step.id}
                className={`w-2 h-2 rounded-full transition-colors ${
                  step.id === currentStep
                    ? "bg-primary"
                    : step.id < currentStep
                    ? "bg-primary/40"
                    : "bg-muted-foreground/20"
                }`}
              />
            ))}
          </div>

          {currentStep < steps.length ? (
            <Button
              size="sm"
              onClick={() => setCurrentStep((s) => Math.min(steps.length, s + 1))}
              disabled={!canGoNext()}
            >
              Suivant <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button size="sm" onClick={handleComplete}>
              <Check className="h-4 w-4 mr-1" /> Terminer
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
